import React from 'react';
import { PlusIcon } from '@heroicons/react/24/outline';
import { 
  ScaleIcon,
  UsersIcon,
  ChartBarIcon,
  BeakerIcon,
  ClockIcon,
  CurrencyDollarIcon,
  TruckIcon,
  FireIcon,
  SparklesIcon,
  CubeIcon
} from '@heroicons/react/24/outline';

// Available icons for metrics
const AVAILABLE_ICONS = [
  { id: 'scale', icon: ScaleIcon, label: 'Scale' },
  { id: 'users', icon: UsersIcon, label: 'Users' },
  { id: 'chart', icon: ChartBarIcon, label: 'Chart' },
  { id: 'beaker', icon: BeakerIcon, label: 'Beaker' },
  { id: 'clock', icon: ClockIcon, label: 'Clock' },
  { id: 'dollar', icon: CurrencyDollarIcon, label: 'Dollar' },
  { id: 'truck', icon: TruckIcon, label: 'Truck' },
  { id: 'fire', icon: FireIcon, label: 'Fire' },
  { id: 'sparkles', icon: SparklesIcon, label: 'Sparkles' },
  { id: 'cube', icon: CubeIcon, label: 'Cube' },
];

// Available background colors
const AVAILABLE_COLORS = [
  { bg: 'bg-green-100', text: 'text-green-600', label: 'Green' },
  { bg: 'bg-sky-100', text: 'text-sky-600', label: 'Sky' },
  { bg: 'bg-fuchsia-100', text: 'text-fuchsia-600', label: 'Fuchsia' },
  { bg: 'bg-amber-100', text: 'text-amber-600', label: 'Amber' },
  { bg: 'bg-red-100', text: 'text-red-600', label: 'Red' },
  { bg: 'bg-indigo-100', text: 'text-indigo-600', label: 'Indigo' },
  { bg: 'bg-purple-100', text: 'text-purple-600', label: 'Purple' },
  { bg: 'bg-pink-100', text: 'text-pink-600', label: 'Pink' },
  { bg: 'bg-teal-100', text: 'text-teal-600', label: 'Teal' },
];

interface DashboardMetric {
  id: string;
  enabled: boolean;
  type: 'totalOutfeedWeight' | 'totalInfeedWeight' | 'avgEmployeeOutfeed' | 'avgEmployeeYield' | 'custom';
  displayName: string;
  icon: string;
  iconBgColor: string;
  iconColor: string;
  defaultValue?: string;
}

interface DashboardMetricsTabProps {
  metrics: DashboardMetric[];
  onChange: (metrics: DashboardMetric[]) => void;
}

const DashboardMetricsTab: React.FC<DashboardMetricsTabProps> = ({ metrics, onChange }) => {
  // Ensure metrics is always an array
  const validMetrics = Array.isArray(metrics) ? metrics : [];

  console.log('[DashboardMetricsTab] Received metrics:', metrics);
  console.log('[DashboardMetricsTab] Valid metrics:', validMetrics);

  const addMetric = () => {
    if (validMetrics.length >= 5) return;
    
    const newMetric: DashboardMetric = {
      id: `metric-${Date.now()}`,
      enabled: true,
      type: 'custom',
      displayName: `Metric ${validMetrics.length + 1}`,
      icon: 'chart',
      iconBgColor: AVAILABLE_COLORS[validMetrics.length % AVAILABLE_COLORS.length].bg,
      iconColor: AVAILABLE_COLORS[validMetrics.length % AVAILABLE_COLORS.length].text,
      defaultValue: '',
    };
    
    onChange([...validMetrics, newMetric]);
  };

  const updateMetric = (index: number, updates: Partial<DashboardMetric>) => {
    const updated = [...validMetrics];
    updated[index] = { ...updated[index], ...updates };
    onChange(updated);
  };

  const removeMetric = (id: string) => {
    onChange(validMetrics.filter(m => m.id !== id));
  };

  const getIconComponent = (iconId: string) => {
    const iconData = AVAILABLE_ICONS.find(i => i.id === iconId);
    return iconData ? iconData.icon : ChartBarIcon;
  };

  return (
    <div className="animate-fadeIn">
      <div className="bg-gradient-to-r from-cyan-50 to-teal-50 rounded-lg p-6 mb-6">
        <h3 className="text-lg font-semibold text-slate-800 mb-2">Summary Metrics Configuration</h3>
        <p className="text-sm text-slate-600">Configure up to 5 summary metrics that appear at the top of the dashboard for this product group.</p>
      </div>

      <div className="space-y-4 max-w-4xl">
        {/* Metrics List */}
        {validMetrics.map((metric, index) => {
          const IconComponent = getIconComponent(metric.icon);
          
          return (
            <div key={metric.id} className="bg-white rounded-lg border-2 border-slate-200 p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center space-x-3">
                  <div className={`${metric.iconBgColor} ${metric.iconColor} p-2 rounded-lg`}>
                    <IconComponent className="h-5 w-5" />
                  </div>
                  <h4 className="text-md font-semibold text-slate-700">Metric {index + 1}</h4>
                </div>
                <div className="flex items-center space-x-3">
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={metric.enabled}
                      onChange={(e) => updateMetric(index, { enabled: e.target.checked })}
                      className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                    />
                    <span className="text-sm text-slate-600">Enabled</span>
                  </label>
                  {validMetrics.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeMetric(metric.id)}
                      className="text-red-600 hover:text-red-700"
                    >
                      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  )}
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">Metric Type</label>
                  <select
                    value={metric.type}
                    onChange={(e) => {
                      const type = e.target.value as any;
                      const displayName = type === 'totalOutfeedWeight' ? 'Total Outfeed Weight' :
                                        type === 'totalInfeedWeight' ? 'Total Infeed Weight' :
                                        type === 'avgEmployeeOutfeed' ? 'Average Employee Outfeed' :
                                        type === 'avgEmployeeYield' ? 'Average Employee Yield' :
                                        metric.displayName;
                      updateMetric(index, { type, displayName });
                    }}
                    className="w-full px-2 py-1.5 text-sm border border-slate-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                    disabled={!metric.enabled}
                  >
                    <option value="totalOutfeedWeight">Total Outfeed Weight</option>
                    <option value="totalInfeedWeight">Total Infeed Weight</option>
                    <option value="avgEmployeeOutfeed">Average Employee Outfeed</option>
                    <option value="avgEmployeeYield">Average Employee Yield</option>
                    <option value="custom">Custom (Future)</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">Display Name</label>
                  <input
                    type="text"
                    value={metric.displayName}
                    onChange={(e) => updateMetric(index, { displayName: e.target.value })}
                    className="w-full px-2 py-1.5 text-sm border border-slate-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="Enter custom display name"
                    disabled={!metric.enabled}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">Icon</label>
                  <div className="flex flex-wrap gap-1">
                    {AVAILABLE_ICONS.map(({ id, icon: Icon, label }) => (
                      <button
                        key={id}
                        type="button"
                        onClick={() => updateMetric(index, { icon: id })}
                        className={`p-1.5 rounded-md border transition-all ${
                          metric.icon === id 
                            ? 'border-indigo-500 bg-indigo-50' 
                            : 'border-slate-200 hover:border-slate-300'
                        }`}
                        disabled={!metric.enabled}
                        title={label}
                      >
                        <Icon className="h-4 w-4" />
                      </button>
                    ))}
                  </div>
                </div>
                
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">Color</label>
                  <div className="flex flex-wrap gap-1">
                    {AVAILABLE_COLORS.map(({ bg, text, label }) => (
                      <button
                        key={bg}
                        type="button"
                        onClick={() => updateMetric(index, { iconBgColor: bg, iconColor: text })}
                        className={`w-6 h-6 rounded-md border transition-all ${bg} ${
                          metric.iconBgColor === bg 
                            ? 'border-slate-800 ring-1 ring-offset-1 ring-slate-400' 
                            : 'border-slate-300 hover:border-slate-400'
                        }`}
                        disabled={!metric.enabled}
                        title={label}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          );
        })}

        {/* Add Metric Button */}
        {validMetrics.length < 5 && (
          <button
            type="button"
            onClick={addMetric}
            className="w-full py-3 border-2 border-dashed border-slate-300 rounded-lg hover:border-indigo-400 hover:bg-indigo-50 transition-all group"
          >
            <div className="flex items-center justify-center space-x-2 text-slate-600 group-hover:text-indigo-600">
              <PlusIcon className="h-4 w-4" />
              <span className="text-sm font-medium">Add Metric</span>
            </div>
          </button>
        )}

        {/* Info Box */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 max-w-4xl">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-4 w-4 text-blue-400 mt-0.5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-2">
              <h3 className="text-xs font-medium text-blue-800">About Summary Metrics</h3>
              <div className="mt-1 text-xs text-blue-700">
                <p>These metrics will appear at the top of the dashboard when this product group is active.</p>
                <ul className="list-disc list-inside mt-1 space-y-0.5">
                  <li><strong>Total Outfeed Weight:</strong> Sum of all outfeed product weights</li>
                  <li><strong>Average Employee Outfeed:</strong> Average outfeed weight per employee</li>
                  <li><strong>Average Employee Yield:</strong> Average yield percentage across all employees</li>
                  <li><strong>Custom:</strong> Define your own metric calculation (coming soon)</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardMetricsTab;